import {
  type QueryFunctionContext,
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysCompany } from '../queryKeys';

import { fetchCompanyAdmin } from './fetchCompanyAdmin';
import { type CompanyAdmin } from './types';

/**
 * Generates the query options for fetching a company with a given companyId as admin.
 *
 * @param companyId - The ID of the company to fetch.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getCompanyAdminQueryOptions = <TData = CompanyAdmin>({
  companyId,
  options,
}: {
  companyId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<CompanyAdmin, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    enabled: Boolean(companyId),
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchCompanyAdmin(companyId!, { signal }),
    queryKey: queryKeysCompany.getAdmin(companyId ?? ''),
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query based custom hook for getting the data for a company with a given companyId as admin.
 * Requires ADMIN access.
 *
 * @param companyId - The ID of the company to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/get_company_by_id_vadmin_company__company_id__get
 */
export const useQueryCompanyAdmin = (
  companyId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<CompanyAdmin, Error, CompanyAdmin>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery<CompanyAdmin, Error, CompanyAdmin>(
    getCompanyAdminQueryOptions<CompanyAdmin>({ companyId, options }),
  );
